/**
 * Product Options Admin Page
 * 
 * Allows Tammy to manage customizable product options:
 * - Create/edit option groups (Bread, Toppings, Sauces, etc.)
 * - Add/remove individual options within groups
 * - Set prices for premium options
 * - Enable/disable options temporarily
 */

import { useState, useEffect } from 'react';
import { 
  HiPlus, 
  HiPencil, 
  HiTrash, 
  HiEye, 
  HiEyeOff,
  HiRefresh,
  HiChevronDown,
  HiChevronRight,
  HiCollection,
  HiCurrencyDollar,
} from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

// Types
interface ProductOption {
  id: string;
  name: string;
  emoji: string | null;
  image_url: string | null;
  price_adjustment: number;
  is_default: boolean;
  is_available: boolean;
  display_order: number;
}

interface OptionGroup {
  id: string;
  name: string;
  description: string | null;
  selection_type: 'single' | 'multi';
  is_required: boolean;
  min_selections: number;
  max_selections: number | null;
  menu_item_id: string | null;
  category_name: string | null;
  is_active: boolean;
  display_order: number;
  options: ProductOption[];
}

// Categories that can have product options
const CATEGORIES = [
  'Sandwiches & Savory',
  'Custom Cakes',
  'Cheesecakes',
  'Tarts',
  'Stuffed Cookies',
];

export default function ProductOptions() {
  const [groups, setGroups] = useState<OptionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  // Modal states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<OptionGroup | null>(null);
  const [editingOption, setEditingOption] = useState<{ option: ProductOption | null; groupId: string } | null>(null);

  // Fetch all option groups
  const fetchGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminFetch('/api/product-options/groups');
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // Toggle group expansion
  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Toggle option availability
  const toggleOptionAvailability = async (optionId: string) => {
    try {
      const response = await adminFetch(`/api/product-options/options/${optionId}/toggle`, {
        method: 'PUT',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to toggle');
      }
      await fetchGroups();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update option';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Delete option
  const deleteOption = async (optionId: string, optionName: string) => {
    if (!confirm(`Delete "${optionName}"? This cannot be undone.`)) return;
    
    try {
      const response = await adminFetch(`/api/product-options/options/${optionId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete');
      }
      await fetchGroups();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete option';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Delete group
  const deleteGroup = async (groupId: string, groupName: string) => {
    if (!confirm(`Delete "${groupName}" and all its options? This cannot be undone.`)) return;
    
    try {
      const response = await adminFetch(`/api/product-options/groups/${groupId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete');
      }
      await fetchGroups();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete group';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Options</h1>
          <p className="text-gray-500 mt-1">
            Customize what customers can add to their orders
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchGroups}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 
                       rounded-lg hover:bg-gray-50 transition-colors"
            title="Refresh"
          >
            <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => {
              setEditingGroup(null);
              setShowGroupModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 
                       text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <HiPlus className="w-5 h-5" />
            Add Group
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm">
        <p className="text-blue-800">
          <strong>💡 Tip:</strong> Option groups can apply to an entire category (like "Sandwiches & Savory") 
          or to a specific menu item. For the public cake request form, use category <strong>Custom Cakes</strong> and
          name the groups Cake Size, Cake Flavour, and Cake Filling. Customers see those as dropdowns on /cake-request.
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
          <button onClick={fetchGroups} className="ml-2 underline">Try again</button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading options...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && groups.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="text-6xl mb-4">🎨</div>
          <h3 className="text-lg font-semibold text-gray-900">No option groups yet</h3>
          <p className="text-gray-500 mt-2">
            Create your first option group to let customers customize their orders.
          </p>
          <button
            onClick={() => {
              setEditingGroup(null);
              setShowGroupModal(true);
            }}
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-purple-600 
                       text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <HiPlus className="w-5 h-5" />
            Create First Group
          </button>
        </div>
      )}

      {/* Option Groups List */}
      {!loading && groups.length > 0 && (
        <div className="space-y-4">
          {groups.map(group => (
            <div key={group.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Group Header */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleGroup(group.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedGroups.has(group.id) ? (
                    <HiChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <HiChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      {group.name}
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        group.selection_type === 'single' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {group.selection_type === 'single' ? 'Pick One' : 'Multi-Select'}
                      </span>
                      {group.is_required && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                          Required
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <HiCollection className="w-4 h-4" />
                      {group.category_name || 'All items'} • {group.options.length} option{group.options.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => {
                      setEditingGroup(group);
                      setShowGroupModal(true);
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Edit group"
                  >
                    <HiPencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteGroup(group.id, group.name)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete group"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Options List (Expanded) */}
              {expandedGroups.has(group.id) && (
                <div className="border-t border-gray-100">
                  <div className="p-4 space-y-2">
                    {group.options.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">
                        No options in this group yet.
                      </p>
                    ) : (
                      // Sort options alphabetically by name (A-Z) for stable display
                      // This ensures items don't move when visibility is toggled
                      [...group.options]
                        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
                        .map(option => (
                        <div 
                          key={option.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            option.is_available ? 'bg-gray-50' : 'bg-gray-100 opacity-60'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Show image if available, otherwise emoji */}
                            {option.image_url ? (
                              <img 
                                src={option.image_url} 
                                alt={option.name}
                                className="w-8 h-8 object-cover rounded-lg"
                              />
                            ) : (
                              <span className="text-2xl">{option.emoji || '⚪'}</span>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">
                                {option.name}
                                {option.is_default && (
                                  <span className="ml-2 text-xs text-purple-600">(default)</span>
                                )}
                              </p>
                              {option.price_adjustment > 0 && (
                                <p className="text-sm text-green-600 flex items-center gap-1">
                                  <HiCurrencyDollar className="w-3 h-3" />
                                  +${option.price_adjustment.toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleOptionAvailability(option.id)}
                              className={`p-2 rounded-lg transition-colors ${
                                option.is_available 
                                  ? 'text-gray-600 hover:bg-gray-200' 
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={option.is_available ? 'Hide option' : 'Show option'}
                            >
                              {option.is_available ? <HiEyeOff className="w-4 h-4" /> : <HiEye className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => {
                                setEditingOption({ option, groupId: group.id });
                                setShowOptionModal(true);
                              }}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                              title="Edit option"
                            >
                              <HiPencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteOption(option.id, option.name)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Delete option"
                            >
                              <HiTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                    
                    {/* Add Option Button */}
                    <button
                      onClick={() => {
                        setEditingOption({ option: null, groupId: group.id });
                        setShowOptionModal(true);
                      }}
                      className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg 
                                 text-gray-500 hover:border-purple-400 hover:text-purple-600 
                                 transition-colors flex items-center justify-center gap-2"
                    >
                      <HiPlus className="w-4 h-4" />
                      Add Option
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <GroupModal
          group={editingGroup}
          categories={CATEGORIES}
          onClose={() => {
            setShowGroupModal(false);
            setEditingGroup(null);
          }}
          onSave={async () => {
            await fetchGroups();
            setShowGroupModal(false);
            setEditingGroup(null);
          }}
        />
      )}

      {/* Option Modal */}
      {showOptionModal && editingOption && (
        <OptionModal
          option={editingOption.option}
          groupId={editingOption.groupId}
          onClose={() => {
            setShowOptionModal(false);
            setEditingOption(null);
          }}
          onSave={async () => {
            await fetchGroups();
            setShowOptionModal(false);
            setEditingOption(null);
          }}
        />
      )}
    </div>
  );
}

// ===========================================
// Group Modal Component
// ===========================================
interface GroupModalProps {
  group: OptionGroup | null;
  categories: string[];
  onClose: () => void;
  onSave: () => void;
}

function GroupModal({ group, categories, onClose, onSave }: GroupModalProps) {
  const [formData, setFormData] = useState({
    name: group?.name || '',
    description: group?.description || '',
    selection_type: group?.selection_type || 'multi',
    is_required: group?.is_required || false,
    category_name: group?.category_name || '',
    display_order: group?.display_order || 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const url = group 
        ? `/api/product-options/groups/${group.id}` 
        : '/api/product-options/groups';
      
      const response = await adminFetch(url, {
        method: group ? 'PUT' : 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...formData,
          category_name: formData.category_name || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save');
      }

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              {group ? 'Edit Option Group' : 'New Option Group'}
            </h2>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="e.g., Toppings, Bread Type, Sauces"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (shown to customers)
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Select your favorite toppings"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apply to Category
              </label>
              <select
                value={formData.category_name}
                onChange={e => setFormData(prev => ({ ...prev, category_name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All customizable items</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Selection Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selection Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, selection_type: 'single' }))}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    formData.selection_type === 'single'
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium">Pick One</p>
                  <p className="text-xs text-gray-500">e.g., bread type</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, selection_type: 'multi' }))}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    formData.selection_type === 'multi'
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium">Multi-Select</p>
                  <p className="text-xs text-gray-500">e.g., toppings</p>
                </button>
              </div>
            </div>

            {/* Required */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_required}
                onChange={e => setFormData(prev => ({ ...prev, is_required: e.target.checked }))}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">
                Required (customer must select at least one)
              </span>
            </label>
          </div>

          <div className="p-6 border-t border-gray-200 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : group ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================
// Option Modal Component
// ===========================================
interface OptionModalProps {
  option: ProductOption | null;
  groupId: string;
  onClose: () => void;
  onSave: () => void;
}

function OptionModal({ option, groupId, onClose, onSave }: OptionModalProps) {
  const [formData, setFormData] = useState({
    name: option?.name || '',
    emoji: option?.emoji || '',
    image_url: option?.image_url || '',
    price_adjustment: option?.price_adjustment?.toString() || '0',
    is_default: option?.is_default || false,
    display_order: option?.display_order || 0,
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [iconMode, setIconMode] = useState<'emoji' | 'image'>(option?.image_url ? 'image' : 'emoji');

  // All available pre-loaded icons
  // These icons are in /assets/icons/toppings/
  const AVAILABLE_ICONS = [
    { name: 'Lettuce', file: 'lettuce.png' },
    { name: 'Tomato', file: 'tomato.png' },
    { name: 'Cucumber', file: 'cucumber.png' },
    { name: 'Pickle', file: 'pickle.png' },
    { name: 'Bell Pepper', file: 'bell-pepper.png' },
    { name: 'Jalapeño', file: 'jalapeno.png' },
    { name: 'Onion', file: 'onion.png' },
    { name: 'Cheese', file: 'cheese.png' },
    { name: 'Bacon', file: 'bacon.png' },
    { name: 'Mustard', file: 'mustard.png' },
    { name: 'Dijon', file: 'dijon.png' },
    { name: 'Mayo', file: 'mayo.png' },
    { name: 'Ketchup', file: 'ketchup.png' },
    { name: 'Butter', file: 'butter.png' },
    { name: 'Wrap', file: 'wrap.png' },
    { name: 'Baguette', file: 'baguette.png' },
  ];

  // Name to icon mapping for auto-suggestion
  const ICON_MAP: Record<string, string> = {
    'lettuce': 'lettuce.png',
    'tomatoes': 'tomato.png',
    'tomato': 'tomato.png',
    'cucumbers': 'cucumber.png',
    'cucumber': 'cucumber.png',
    'pickles': 'pickle.png',
    'pickle': 'pickle.png',
    'banana peppers': 'bell-pepper.png',
    'jalapeño peppers': 'jalapeno.png',
    'jalapeno peppers': 'jalapeno.png',
    'jalapeño': 'jalapeno.png',
    'jalapeno': 'jalapeno.png',
    'mustard': 'mustard.png',
    'dijon mustard': 'dijon.png',
    'dijon': 'dijon.png',
    'mayo': 'mayo.png',
    'mayonnaise': 'mayo.png',
    'ketchup': 'ketchup.png',
    'wrap': 'wrap.png',
    'baguette': 'baguette.png',
    'cheese': 'cheese.png',
    'bacon': 'bacon.png',
    'onion': 'onion.png',
    'onions': 'onion.png',
    'butter': 'butter.png',
  };

  // Get pre-loaded icon URL for current option name
  const getPreloadedIcon = (name: string): string | null => {
    const key = name.toLowerCase().trim();
    const filename = ICON_MAP[key];
    return filename ? `/assets/icons/toppings/${filename}` : null;
  };

  // Check if a pre-loaded icon exists for this option
  const preloadedIcon = getPreloadedIcon(formData.name);

  // Handle switching between emoji and image mode
  const handleModeSwitch = (mode: 'emoji' | 'image') => {
    setIconMode(mode);
    if (mode === 'image' && preloadedIcon) {
      // Auto-set the pre-loaded icon when switching to image mode
      setFormData(prev => ({ ...prev, image_url: preloadedIcon }));
    }
  };

  // Common emojis for food options
  const COMMON_EMOJIS = ['🥬', '🍅', '🥒', '🧅', '🫑', '🌶️', '🧀', '🥓', '🍳', '🌯', '🥖', '🟡', '⚪', '🔴', '🟢'];

  // Handle image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await adminFetch('/api/upload', {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFormData(prev => ({ ...prev, image_url: data.url, emoji: '' }));
    } catch (_err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Clear image
  const clearImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const url = option 
        ? `/api/product-options/options/${option.id}` 
        : '/api/product-options/options';
      
      const response = await adminFetch(url, {
        method: option ? 'PUT' : 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...formData,
          group_id: groupId,
          price_adjustment: parseFloat(formData.price_adjustment) || 0,
          // Clear emoji if using image, clear image if using emoji
          emoji: iconMode === 'emoji' ? formData.emoji : null,
          image_url: iconMode === 'image' ? formData.image_url : null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save');
      }

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              {option ? 'Edit Option' : 'New Option'}
            </h2>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Option Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="e.g., Lettuce, Wrap, Mayo"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Icon Type Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icon Type
              </label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => handleModeSwitch('emoji')}
                  className={`p-2 border-2 rounded-lg text-center transition-all ${
                    iconMode === 'emoji'
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-xl">😀</span>
                  <p className="text-xs text-gray-600">Emoji</p>
                </button>
                <button
                  type="button"
                  onClick={() => handleModeSwitch('image')}
                  className={`p-2 border-2 rounded-lg text-center transition-all ${
                    iconMode === 'image'
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-xl">🖼️</span>
                  <p className="text-xs text-gray-600">Image</p>
                  {preloadedIcon && iconMode !== 'image' && (
                    <p className="text-xs text-green-600">✓ Available</p>
                  )}
                </button>
              </div>
            </div>

            {/* Emoji Selector (if emoji mode) */}
            {iconMode === 'emoji' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Emoji
                </label>
                <div className="flex gap-2 flex-wrap mb-2">
                  {COMMON_EMOJIS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, emoji }))}
                      className={`w-10 h-10 text-xl rounded-lg border-2 transition-all ${
                        formData.emoji === emoji 
                          ? 'border-purple-600 bg-purple-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.emoji}
                  onChange={e => setFormData(prev => ({ ...prev, emoji: e.target.value }))}
                  placeholder="Or type your own emoji"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}

            {/* Image Upload (if image mode) */}
            {iconMode === 'image' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Choose an Icon
                </label>
                
                {/* Grid of available icons to pick from */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {AVAILABLE_ICONS.map(icon => {
                    const iconUrl = `/assets/icons/toppings/${icon.file}`;
                    const isSelected = formData.image_url === iconUrl;
                    return (
                      <button
                        key={icon.file}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: iconUrl }))}
                        className={`p-2 border-2 rounded-lg flex flex-col items-center transition-all ${
                          isSelected
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        title={icon.name}
                      >
                        <img 
                          src={iconUrl} 
                          alt={icon.name}
                          className="w-8 h-8 object-contain"
                        />
                        <span className="text-xs text-gray-600 mt-1 truncate w-full text-center">
                          {icon.name}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Current selection preview */}
                {formData.image_url && (
                  <div className="mb-3 flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <img 
                      src={formData.image_url} 
                      alt="Selected icon" 
                      className="w-10 h-10 object-contain"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-green-700 font-medium">
                        ✓ Selected
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={clearImage}
                      className="text-xs text-gray-500 hover:text-red-600"
                    >
                      Clear
                    </button>
                  </div>
                )}

                {/* Upload button */}
                <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed 
                                   rounded-lg cursor-pointer transition-colors
                                   ${uploading 
                                     ? 'border-gray-300 bg-gray-50' 
                                     : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'}`}
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-gray-600">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <HiPlus className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-600">
                        {formData.image_url ? 'Replace image' : 'Upload image'}
                      </span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Recommended: Square image, max 2MB. PNG or JPG.
                </p>
              </div>
            )}

            {/* Price Adjustment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Extra Cost (CAD)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={formData.price_adjustment}
                  onChange={e => setFormData(prev => ({ ...prev, price_adjustment: e.target.value }))}
                  min="0"
                  step="0.25"
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Leave at $0 for free options
              </p>
            </div>

            {/* Default */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={e => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">
                Pre-selected by default
              </span>
            </label>
          </div>

          <div className="p-6 border-t border-gray-200 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : option ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

